import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  getDateTimeObjFromDateTimeStr,
  getDateDetailsFromDateTimeObj,
  getTimeDetailsFromDateTimeObj,
} from 'utils/functions/dateTime';
import { trimText } from 'utils/functions/general';
import { closedCirclesListActionsEnum } from 'modules/circles/enums';
import circlesStrings from 'modules/circles/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemColumnWithHover,
  ItemRow,
  ItemText,
  ItemClick,
  ItemHoverWrapper,
  ItemHover,
  ItemTag,
  ItemBtnWithText,
  /* ####################### */
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './ClosedCirclesList.style';

const ClosedCirclesListActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionPopupStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {
            circlesStrings.closedCirclesList[
              `${closedCirclesListActionsEnum.getKeyByValue(actionPopupStatus.key)}${
                actionPopupStatus.value
              }ActionPopupTitle`
            ]
          }
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {circlesStrings.closedCirclesList.closedCirclesListActionsPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {circlesStrings.closedCirclesList.closedCirclesListActionsPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
ClosedCirclesListActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.shape({
    key: PropTypes.number,
    value: PropTypes.bool,
  }).isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const ClosedCirclesList = ({
  closedCirclesListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  handleTextToCopyToClipboardClick,
  handlePreviewFileClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const closedCirclesGridHeaderList = circlesStrings.closedCirclesList.closedCirclesGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(closedCirclesGridHeaderList).map(closedCirclesGridHeaderKey => (
            <Header key={closedCirclesGridHeaderKey}>
              {closedCirclesGridHeaderList[closedCirclesGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {closedCirclesListArr.map((circle, index) => {
            const { circleKey, name, description, logoUrl, createdDate, ownerFeed } = circle;
            const { profile } = ownerFeed;
            const { name: authorName } = profile;

            const postDateTimeObj = getDateTimeObjFromDateTimeStr(createdDate, true);
            const { dateObj } = postDateTimeObj;
            const localDateObj = new Date(dateObj.setHours(dateObj.getHours() + 3));
            const { day, month, year } = getDateDetailsFromDateTimeObj(localDateObj);
            const { hours, minutes, period } = getTimeDetailsFromDateTimeObj(localDateObj);
            const postDateTimeStr = `${day} ${month} ${year} ${hours}:${minutes} ${period}`;

            const isLastItem = index === closedCirclesListArr.length - 1;

            return (
              <React.Fragment key={circleKey}>
                <ItemColumn>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(circleKey)}>
                    {trimText(circleKey, 10)}
                  </ItemClick>
                </ItemColumn>
                <ItemColumnWithHover>
                  <ItemText>{trimText(name, 50)}</ItemText>
                  <ItemHoverWrapper>
                    <ItemHover>{name}</ItemHover>
                  </ItemHoverWrapper>
                </ItemColumnWithHover>
                <ItemColumnWithHover>
                  <ItemText>{trimText(description, 100)}</ItemText>
                  <ItemHoverWrapper>
                    <ItemHover>{description}</ItemHover>
                  </ItemHoverWrapper>
                </ItemColumnWithHover>
                <ItemRow>
                  <ItemTag hover onClick={() => handlePreviewFileClick(true, 'image', logoUrl)}>
                    {circlesStrings.closedCirclesList.imageFileText}
                  </ItemTag>
                </ItemRow>
                <ItemColumn>
                  <ItemText>{postDateTimeStr}</ItemText>
                </ItemColumn>
                <ItemColumnWithHover>
                  <ItemText>{trimText(authorName, 50)}</ItemText>
                  <ItemHoverWrapper>
                    <ItemHover>{authorName}</ItemHover>
                  </ItemHoverWrapper>
                </ItemColumnWithHover>
                <ItemRow>
                  <ItemBtnWithText
                    as="button"
                    icon={'"\\e91f"'}
                    color={colors.greenMain}
                    backgroundColor={colors.greenLight}
                    onClick={() =>
                      handleActionBtnClick(circleKey, {
                        key: closedCirclesListActionsEnum.approve,
                        value: true,
                      })
                    }
                  >
                    {circlesStrings.closedCirclesList.approveBtnText}
                  </ItemBtnWithText>
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <ClosedCirclesListActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionPopupStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

ClosedCirclesList.propTypes = {
  closedCirclesListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.shape({}).isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

ClosedCirclesList.defaultProps = {};

export default ClosedCirclesList;
